import React, { useEffect, useState } from "react";
import { collection, query, where, getDocs, addDoc } from "firebase/firestore";
import { toast } from 'react-toastify';
import { db } from "../../firebase-config";

const OBELISCO_COORDS = { lat: -34.603722, lng: -58.381592 };

interface Project {
  id?: string;
  Name?: string;
  identifier?: string;
  address?: string;
  orientation?: string;
  coordinates?: { lat: number; lng: number };  
  //logo?: string;
  displayOrder?: number;
  //city?: string;
  visible?: boolean;
  //instagram?: string;
  featured?: boolean;
  simNumber?: string;
  customerId?: string;
  responsibleUser?: number;
  active?: boolean;
  status?: "Creado" | "Aprobado" | "Terminado" | "Suspendida" | "";
  [key: string]: any;
}

interface ProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (project: Project) => void;
  initialProject?: Project;
  customerId: string;
}

const NewProject = ({ isOpen, onClose, onSave, customerId }: ProjectModalProps) => {

  const initialProjectState: Project = {
    Name: "",
    identifier: "",
    //address: "",
    orientation: "",
    coordinates: OBELISCO_COORDS,
    //logo: "",
    displayOrder: 0,
    visible: false,
    //instagram: "",
    featured: false,
    simNumber: "",
    customerId: customerId,
    active: true,
    status: "Creado",
    responsibleUser: 0,
  };

  const [project, setProject] = useState<Project>(initialProjectState);
  const [blocked, setBlocked] = useState(false);

  const projectCollection = collection(db, 'usuarios');
  const MAX_ATTEMPTS = 10;

  const requiredFields = [
    "Name", 
    //"address", 
    //"city", 
    //"simNumber"
  ];


  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    if (isOpen) {
      window.addEventListener("keydown", handleEsc);
      return () => window.removeEventListener("keydown", handleEsc);
    }
  }, [isOpen, onClose]);

  
  const generateUniqueIdentifier = async (Name: string): Promise<string | null> => {
    let newIdentifier: string | null = null;
    let exists = true;
    let attempts = 0;
    let prefix = Name.trim().substring(0, 3).toLowerCase();
    console.log("Prefix:", prefix);
    if (prefix.length === 2) {
        prefix += "_";
      }

    while (exists && attempts < MAX_ATTEMPTS) {
      const randomNumbers = Math.floor(100 + Math.random() * 900);
      newIdentifier = `P_${prefix}${randomNumbers}`;
      exists = await checkIfIdentifierExists(newIdentifier);
      attempts++;
    }

    if (!exists) {
      return newIdentifier;
    } else {
      console.error("Se alcanzó el límite de intentos para generar un identifier único.");
      return null;
    }
  };

  const checkIfIdentifierExists = async (identifierToCheck) => {
    try {
      const q = query(projectCollection, where('identifier', '==', identifierToCheck));
      const querySnapshot = await getDocs(q);
      return !querySnapshot.empty;
    } catch (error) {
      console.error("Error al verificar el identifier:", error);
      return true;
    }
  };

  const fieldMetadata: Record<string, { label: string; description: string; type?: 'text' | 'number' | 'boolean' | 'select'; options?: string[] }> = {
    Name: { label: "Nombre*", description: "Nombre del proyecto", type: 'text' },
    //address: { label: "Calle, número*", description: "Ubicación física del proyecto", type: 'text' },
    //city: { label: "Ciudad*", description: "Ciudad del proyecto", type: 'text' },
    orientation: { label: "Orientación", description: "Orientación de la cámara", type: 'text' },
    //coordinates: { label: "Coordenadas", description: "Coordenadas geográficas", type: 'text' },
    logo: { label: "Logo", description: "URL del logo", type: 'text' },
    //instagram: { label: "Instagram (ej. @lisual.ok)*", description: "Cuenta de Instagram asociada", type: 'text' },
    simNumber: { label: "SIM o número de linea", description: "Número de tarjeta SIM", type: 'text' },
    featured: { label: "¿Queres que tu proyecto sea visible por la comunidad?", description: "¿Es un proyecto destacado?", type: 'boolean' },
    visible: { label: "Visible", description: "¿El proyecto es visible?", type: 'boolean' },
    responsibleUser: { label: "Telefono del Responsable (ej. 5491112345678)", description: "Telefono del Responsable", type: 'number' },
    active: { label: "Activo", description: "Indica si el proyecto está activo", type: 'boolean' },
    status: { label: "Estado", description: "Estado actual del proyecto", type: 'select', options: ['Creado', 'Aprobado', 'Terminado', ''] }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    if (e.target instanceof HTMLInputElement && type === 'checkbox') {
      const target = e.target as HTMLInputElement;
      setProject(prev => ({ ...prev, [name]: target.checked }));
      return;
    }

    if (name === "responsibleUser") {
      const numericValue = value.replace(/[^0-9]/g, ''); 
      if (numericValue === '' || /^\d*$/.test(numericValue)) { 
        setProject(prev => ({ ...prev, [name]: numericValue === '' ? 0 : Number(numericValue) }));
      }
      return; 
    }

    setProject(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {

    const missingFields = requiredFields.filter(field => {
      const value = project[field];
      return value === undefined || value === null || value.toString().trim() === "";
    });
  
    if (missingFields.length > 0) {
      toast.error("Por favor completá todos los campos obligatorios marcados con *");
      return;
    }

    setBlocked(true);
    const identifier = await generateUniqueIdentifier(project.Name || "");

    if (identifier) {
      const updatedProject = { ...project, identifier, customerId };

      try {
        console.log("Guardando proyecto:", updatedProject);
        await addDoc(projectCollection, updatedProject);
        
        onSave(updatedProject);

        toast.success('Proyecto creado. Esperando aprobación.');
        
            onClose();

            setProject(initialProjectState);
            setBlocked(false);
        
        
        
      } catch (error) {
        console.error("Error al guardar el proyecto:", error);
        toast.error("Hubo un error al guardar el proyecto");
      }
    } else {
      toast.error("No se pudo generar un identificador único.");
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center p-4 z-50 overflow-y-auto"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="relative bg-white rounded-lg w-full max-w-xl mt-16 mb-8">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 text-gray-400 hover:text-gray-500"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
          <p><strong>Nueva Cámara</strong></p>
          <p>Repetí el proceso tantas veces como cámaras compraste.</p>
        </div>
        <div className="p-6 space-y-4 max-h-[80vh] overflow-y-auto">
          {[
            "Name", 
            //"address", 
            //"city", 
            "simNumber", 
            "responsibleUser"
          ].map((name) => (
            <div key={name}>
              <label className="block font-medium text-sm mb-1">{fieldMetadata[name]?.label || name}</label>
              <input
                type="text"
                name={name}
                value={project[name] || ""}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded px-3 py-2"
              />
            </div>
          ))}

          {["featured"].map((field) => (
            <div key={field} className="flex items-center space-x-2">
              <input
                type="checkbox"
                name={field}
                checked={!!project[field]}
                onChange={handleChange}
              />
              <label>{fieldMetadata[field]?.label || field}</label>
            </div>
          ))}

          <div className="flex justify-end space-x-2 pt-4">
            <button onClick={onClose} className="px-4 py-2 bg-gray-300 rounded">
              Cancelar
            </button>
            <button
                onClick={handleSubmit}
                className={`px-4 py-2 ${blocked ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-500'} text-white rounded`}
                disabled={blocked}
                >
                {blocked ? "Guardando..." : "Guardar"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewProject;
